<script setup lang="ts">
import { onMounted } from "vue";
import { RouterLink, RouterView } from "vue-router";
import { useControlStore } from "@/stores/control";
import { useAccessStore } from "@/stores/access";
import { usePolling } from "@/composables/usePolling";
import ModeBadge from "@/components/atoms/ModeBadge.vue";
import BuildIdBadge from "@/components/atoms/BuildIdBadge.vue";
import ToastRegion from "@/components/organisms/ToastRegion.vue";
import AccessSignInBanner from "@/components/organisms/AccessSignInBanner.vue";

// REQ-CD-P1-CON-01 — the header's ModeBadge shows the live effective mode on
// every route. ControlView polls the same store at the 5s /control cadence
// (D-CD-33) while it's mounted; the shell polls at 30s (the "/" route's
// cadence) so the header stays live without doubling the request rate when
// /control isn't the active view.
const control = useControlStore();
const access = useAccessStore();
usePolling(() => control.fetchStatus(), 30_000);
// REQ-CD-P1-CON-10 / D-CD-60 — recovery poll for the sign-in banner; also
// fires immediately on visibilitychange -> visible, which is when the
// operator returns from the sign-in tab.
usePolling(() => access.recheck(), 30_000);

onMounted(() => {
  void control.fetchStatus();
});

const buildId = __BUILD_ID__;
</script>

<template>
  <a
    href="#main-content"
    class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-brand-blue focus:px-4 focus:py-2 focus:text-white"
  >
    Skip to content
  </a>

  <header class="border-b border-brand-slate/20 bg-card">
    <div class="mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div class="flex items-center gap-4">
        <span class="text-sm font-semibold text-slate-100">Cyberfide Sentinel</span>
        <nav aria-label="Primary" class="flex flex-wrap gap-3 text-sm">
          <RouterLink to="/" class="min-h-[44px] px-2 py-2 text-brand-light hover:text-slate-100" active-class="text-slate-100 font-medium">
            Status
          </RouterLink>
          <RouterLink
            to="/assets"
            class="min-h-[44px] px-2 py-2 text-brand-light hover:text-slate-100"
            active-class="text-slate-100 font-medium"
          >
            Assets
          </RouterLink>
          <RouterLink
            to="/incidents"
            class="min-h-[44px] px-2 py-2 text-brand-light hover:text-slate-100"
            active-class="text-slate-100 font-medium"
          >
            Incidents
          </RouterLink>
          <RouterLink
            to="/proposals"
            class="min-h-[44px] px-2 py-2 text-brand-light hover:text-slate-100"
            active-class="text-slate-100 font-medium"
          >
            Proposals
          </RouterLink>
          <RouterLink
            to="/control"
            class="min-h-[44px] px-2 py-2 text-brand-light hover:text-slate-100"
            active-class="text-slate-100 font-medium"
          >
            Control
          </RouterLink>
        </nav>
      </div>
      <div class="flex items-center gap-2">
        <ModeBadge :mode="control.status?.mode.effective ?? null" />
        <BuildIdBadge :build-id="buildId" />
      </div>
    </div>
  </header>

  <AccessSignInBanner />

  <main id="main-content" tabindex="-1">
    <RouterView />
  </main>

  <ToastRegion />
</template>
